import React, { useMemo, useRef } from "react";
import { Flag } from "lucide-react";

const DAY = 86400000;
const ROW = 26;
const LABEL_W = 260;
const RAG = { Complete: "#059669", "In Progress": "#2563EB", Delayed: "#DC2626", "On Hold": "#64748B", "Not Started": "#94A3B8" };
const CRITICAL = "#DC2626";

const monthLabel = (d) => d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });

/**
 * CSS/SVG Gantt: summary bars, milestones, baseline ghosts, dependency arrows and the
 * critical path. No charting dependency.
 */
export const Gantt = ({ tasks, showCritical = true, showLinks = true, showBaseline = true }) => {
  const wrapRef = useRef(null);
  const scale = useMemo(() => {
    const dates = tasks
      .flatMap((t) => [t.start_date, t.end_date, showBaseline ? t.baseline_start : null, showBaseline ? t.baseline_end : null])
      .filter(Boolean)
      .map((d) => new Date(d).getTime());
    if (dates.length === 0) return null;
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    min.setDate(1);
    max.setMonth(max.getMonth() + 1, 0);
    const span = Math.max((max - min) / DAY, 1);
    const months = [];
    const cursor = new Date(min);
    while (cursor <= max) {
      const next = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      months.push({ label: monthLabel(cursor), width: ((Math.min(next, max) - cursor) / DAY / span) * 100 });
      cursor.setMonth(cursor.getMonth() + 1, 1);
    }
    return { min, span, months };
  }, [tasks, showBaseline]);

  if (!scale) {
    return (
      <p data-testid="gantt-empty" className="py-8 text-center text-sm text-slate-400">
        Add dates to your tasks to see the timeline.
      </p>
    );
  }

  const pct = (d) => ((new Date(d).getTime() - scale.min) / DAY / scale.span) * 100;
  const todayPct = ((Date.now() - scale.min) / DAY / scale.span) * 100;
  const rowIndex = Object.fromEntries(tasks.map((t, i) => [t.id, i]));

  const links = showLinks
    ? tasks.flatMap((t) =>
        (t.predecessors || []).map((p) => {
          const from = tasks.find((x) => x.id === p.task_id);
          if (!from || !from.start_date || !t.start_date) return null;
          const fromEnd = p.type === "SS" || p.type === "SF" ? from.start_date : (from.end_date || from.start_date);
          const toStart = p.type === "FF" || p.type === "SF" ? (t.end_date || t.start_date) : t.start_date;
          return {
            key: `${p.task_id}-${t.id}-${p.type}`,
            x1: pct(fromEnd), y1: rowIndex[from.id],
            x2: pct(toStart), y2: rowIndex[t.id],
            critical: showCritical && from.is_critical && t.is_critical,
          };
        }).filter(Boolean))
    : [];

  return (
    <div className="overflow-x-auto" data-testid="gantt-chart" ref={wrapRef}>
      <div className="min-w-[880px]">
        <div className="flex border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wider text-slate-500"
          style={{ paddingLeft: LABEL_W }}>
          {scale.months.map((m, i) => (
            <div key={`${m.label}-${i}`} style={{ width: `${m.width}%` }} className="border-l border-slate-100 px-1 py-1">
              {m.label}
            </div>
          ))}
        </div>

        <div className="relative">
          {todayPct >= 0 && todayPct <= 100 && (
            <div data-testid="gantt-today-marker" className="pointer-events-none absolute top-0 bottom-0 z-20 w-px bg-red-400"
              style={{ left: `calc(${LABEL_W}px + ${todayPct}% * (100% - ${LABEL_W}px) / 100%)` }} />
          )}

          {links.length > 0 && (
            <div data-testid="gantt-links" className="pointer-events-none absolute z-10"
              style={{ left: LABEL_W, top: 0, right: 0, height: tasks.length * ROW }}>
              {links.map((l) => {
                const y1 = l.y1 * ROW + ROW / 2;
                const y2 = l.y2 * ROW + ROW / 2;
                const colour = l.critical ? CRITICAL : "#94A3B8";
                const elbow = `calc(${l.x1}% + 6px)`;
                return (
                  <React.Fragment key={l.key}>
                    <div data-testid={`gantt-link-${l.key}`} className="absolute"
                      style={{ left: `${l.x1}%`, top: y1, width: "6px", height: "1px", backgroundColor: colour }} />
                    <div className="absolute"
                      style={{ left: elbow, top: Math.min(y1, y2), width: "1px", height: Math.abs(y2 - y1) || 1, backgroundColor: colour }} />
                    <div className="absolute"
                      style={{ left: elbow, top: y2, width: `calc(${l.x2}% - ${l.x1}% - 6px)`, height: "1px", backgroundColor: colour }} />
                    <div className="absolute h-0 w-0"
                      style={{ left: `calc(${l.x2}% - 4px)`, top: y2 - 2.5,
                               borderTop: "2.5px solid transparent", borderBottom: "2.5px solid transparent",
                               borderLeft: `4px solid ${colour}` }} />
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {tasks.map((t) => {
            const start = t.start_date ? new Date(t.start_date).getTime() : null;
            const end = t.end_date ? new Date(t.end_date).getTime() : start;
            const left = start ? pct(t.start_date) : 0;
            const width = start ? Math.max(((end - start) / DAY / scale.span) * 100, 0.6) : 0;
            const colour = showCritical && t.is_critical ? CRITICAL : (RAG[t.status] || RAG["Not Started"]);
            const indent = Math.max((t.outline_level || 1) - 1, 0) * 12;
            return (
              <div key={t.id} data-testid={`gantt-row-${t.id}`} className="flex items-center border-b border-slate-50 hover:bg-slate-50"
                style={{ height: ROW }}>
                <div className="flex shrink-0 items-center gap-1.5 pr-3 text-xs" style={{ width: LABEL_W, paddingLeft: indent }}>
                  {t.is_milestone && <Flag size={11} className="shrink-0 text-amber-500" />}
                  <span className={`truncate ${t.is_summary ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`} title={`${t.wbs || ""} ${t.name}`}>
                    {t.name}
                  </span>
                </div>
                <div className="relative h-full flex-1">
                  {showBaseline && t.baseline_start && (
                    <div className="absolute bottom-1 h-1 rounded bg-slate-300"
                      style={{ left: `${pct(t.baseline_start)}%`,
                               width: `${Math.max(pct(t.baseline_end || t.baseline_start) - pct(t.baseline_start), 0.4)}%` }}
                      title={`Baseline ${t.baseline_start} → ${t.baseline_end || t.baseline_start}`} />
                  )}
                  {start && (t.is_milestone ? (
                    <div className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rotate-45"
                      style={{ left: `${left}%`, backgroundColor: colour }} title={`${t.name} — ${t.start_date}`} />
                  ) : t.is_summary ? (
                    <div className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-sm"
                      style={{ left: `${left}%`, width: `${width}%`, backgroundColor: "#334155" }}
                      title={`${t.name} — ${t.start_date} to ${t.end_date}`} />
                  ) : (
                    <div className="absolute top-1/2 h-4 -translate-y-1/2 overflow-hidden rounded"
                      style={{ left: `${left}%`, width: `${width}%`, backgroundColor: `${colour}33` }}
                      title={`${t.name} — ${t.start_date} to ${t.end_date} (${t.percent_complete || 0}%${t.is_critical ? ", critical path" : ""})`}>
                      <div className="h-full rounded-l" style={{ width: `${t.percent_complete || 0}%`, backgroundColor: colour }} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-slate-500" style={{ paddingLeft: LABEL_W }}>
          {Object.entries(RAG).map(([label, colour]) => (
            <span key={label} className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: colour }} /> {label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CRITICAL }} /> Critical path</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-1 w-3 rounded bg-slate-300" /> Baseline</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-px bg-red-400" /> Today</span>
        </div>
      </div>
    </div>
  );
};
